import {useState, useEffect} from "react";
import {Link, useLocation} from "react-router-dom";
import {useModal} from "@/context/ModalContext";
import {useSiteSettings} from "@/context/SiteSettingsContext";
import {phoneHref} from "@/utils/contactHelpers";
import Container from "@components/layout/Container";

const links = [
	{to: "/", label: "Головна"},
	{to: "/catalog", label: "Каталог"},
	{to: "/gallery", label: "Галерея"},
	{to: "/services", label: "Послуги"},
	{to: "/about", label: "Про нас"},
	{to: "/contacts", label: "Контакти"},
];

export default function Navbar() {
	const [mobileOpen, setMobileOpen] = useState(false);
	const [scrolled, setScrolled] = useState(false);
	const {open} = useModal();
	const location = useLocation();
	const {phones, email} = useSiteSettings();

	useEffect(() => {
		setMobileOpen(false);
	}, [location]);

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 12);
		onScroll();
		window.addEventListener("scroll", onScroll, {passive: true});
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	useEffect(() => {
		document.body.style.overflow = mobileOpen ? "hidden" : "";
		return () => {
			document.body.style.overflow = "";
		};
	}, [mobileOpen]);

	useEffect(() => {
		if (!mobileOpen) return;
		const onKey = (e) => {
			if (e.key === "Escape") setMobileOpen(false);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [mobileOpen]);

	const closeMenu = () => setMobileOpen(false);
	const openOrder = () => {
		setMobileOpen(false);
		open();
	};

	return (
		<>
			<nav className={`nav${scrolled ? " scrolled" : ""}`}>
				<Container>
					<Link
						to="/"
						className="nav-logo"
						onClick={(e) => {
							if (location.pathname === "/") {
								e.preventDefault();
							}
							window.scrollTo({top: 0, behavior: "smooth"});
						}}
					>
						{"Гранітна\nмайстерня"}
					</Link>

					<ul className="nav-links">
						{links.map((l) => (
							<li key={l.to}>
								<Link
									to={l.to}
									className={location.pathname === l.to ? "active" : ""}
								>
									{l.label}
								</Link>
							</li>
						))}
					</ul>
					<div className="flex gap-4 items-center">
						<button className="nav-cta" onClick={open} aria-label="Замовити розрахунок">
							<span className="nav-cta-text">Замовити розрахунок</span>
							<svg className="nav-cta-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" /></svg>
						</button>

						<button
							type="button"
							className={`nav-burger${mobileOpen ? " is-open" : ""}`}
							aria-label={mobileOpen ? "Закрити меню" : "Відкрити меню"}
							aria-expanded={mobileOpen}
							aria-controls="mobile-menu"
							onClick={() => setMobileOpen((v) => !v)}
						>
							<span />
							<span />
							<span />
						</button>
					</div>
				</Container>
			</nav>

			<div
				className={`nav-mobile-backdrop${mobileOpen ? " open" : ""}`}
				onClick={closeMenu}
				aria-hidden="true"
			/>

			<aside
				id="mobile-menu"
				className={`nav-mobile${mobileOpen ? " open" : ""}`}
				role="dialog"
				aria-modal="true"
				aria-label="Головне меню"
			>

				<nav className="nav-mobile-links">
					{links.map((l, i) => (
						<Link
							key={l.to}
							to={l.to}
							className={`nav-mobile-link${location.pathname === l.to ? " active" : ""}`}
							style={{transitionDelay: mobileOpen ? `${80 + i * 40}ms` : "0ms"}}
						>
							<span className="nav-mobile-link-text">{l.label}</span>
							<svg className="nav-mobile-link-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
								<path d="M5 12h14M13 6l6 6-6 6" />
							</svg>
						</Link>
					))}
				</nav>

				<div className="nav-mobile-foot">
					<button className="btn btn-navy nav-mobile-cta" onClick={openOrder}>
						Замовити розрахунок
					</button>

					<div className="nav-mobile-contact">
						{phones?.[0] && (
							<a className="nav-mobile-contact-row" href={phoneHref(phones[0])}>
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
									<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
								</svg>
								<span>{phones[0]}</span>
							</a>
						)}
						{email && (
							<a className="nav-mobile-contact-row" href={`mailto:${email}`}>
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
									<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
									<path d="M22 6l-10 7L2 6" />
								</svg>
								<span>{email}</span>
							</a>
						)}
					</div>
				</div>
			</aside>
		</>
	);
}
